import { Check, X, ImageIcon, Film, Loader2, Tag } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ValidateCouponResponse } from "@/lib/api/payments";
import {
  PLANS,
  SHARED_FEATURES,
  PLAN_SPECIFIC_FEATURES,
  ANNUAL_DISCOUNT,
} from "@/config/plans";
import type { PlanId } from "@/config/plans";
import { GENERATION_ESTIMATES, estimateRange, COUPON_BLOCKED_FOR_ANNUAL } from "@/config/pricing";
import { useAuth } from "@/contexts/AuthContext";
import { usePricingCards } from "./usePricingCards";
import type { Plan } from "@/pages/Pricing/types";

/** Toggle to surface the coupon input on the Pricing page. */
const SHOW_COUPON_UI = false;

/** Compute the discounted first-period price for a plan when a coupon is active. */
function computeDiscount(
  plan: Plan,
  displayPrice: number,
  annualTotal: number,
  couponResult: ValidateCouponResponse | null | undefined,
  billingCycle: "monthly" | "annual",
  language: "he" | "en",
): { hasDiscount: boolean; discountedPrice: number; discountedAnnualTotal: number } {
  const noDiscount = { hasDiscount: false, discountedPrice: displayPrice, discountedAnnualTotal: annualTotal };

  if (!couponResult?.valid || plan.monthlyPrice === 0) return noDiscount;
  if (COUPON_BLOCKED_FOR_ANNUAL && billingCycle === "annual") return noDiscount;

  if (couponResult.discountPercent && couponResult.discountPercent > 0) {
    const pct = couponResult.discountPercent;
    return {
      hasDiscount: true,
      discountedPrice: Math.max(0, Math.ceil(displayPrice * (1 - pct / 100))),
      discountedAnnualTotal: billingCycle === "annual"
        ? Math.max(0, Math.ceil(annualTotal * (1 - pct / 100)))
        : annualTotal,
    };
  }

  if (couponResult.discountAmount && couponResult.discountAmount > 0) {
    // Fixed-amount coupons are ILS only — can't apply to USD display
    if (language !== "he") return noDiscount;

    if (billingCycle === "monthly") {
      return {
        hasDiscount: true,
        discountedPrice: Math.max(0, Math.ceil(displayPrice - couponResult.discountAmount)),
        discountedAnnualTotal: annualTotal,
      };
    }
    const discAnnual = Math.max(0, Math.ceil(annualTotal - couponResult.discountAmount));
    return {
      hasDiscount: true,
      discountedPrice: Math.max(0, Math.ceil(discAnnual / 12)),
      discountedAnnualTotal: discAnnual,
    };
  }

  return noDiscount;
}

interface PricingCardsProps {
  /** "landing" links CTAs to /pricing; "full" calls onSelectPlan */
  mode: "landing" | "full";
  /** Called when a plan CTA is clicked (only used in "full" mode) */
  onSelectPlan?: (
    planId: string,
    planName: string,
    billingCycle: "monthly" | "annual",
  ) => void;
  /** Disable all CTA buttons (e.g., while processing payment) */
  disabled?: boolean;
  /** Coupon props (only used in "full" mode) */
  couponCode?: string;
  onCouponCodeChange?: (code: string) => void;
  couponLoading?: boolean;
  couponResult?: ValidateCouponResponse | null;
  onApplyCoupon?: () => void;
  onClearCoupon?: () => void;
  /** Scheduled plan change fields (only used in "full" mode) */
  scheduledPlanId?: string | null;
  scheduledBillingPeriod?: string | null;
  /** Called when user wants to cancel the scheduled change (only used in "full" mode) */
  onCancelScheduledChange?: () => void;
  /** Plan ids to hide (e.g. ["free"] in the credit-wall modal). */
  excludePlanIds?: string[];
}

export function PricingCards({
  mode,
  onSelectPlan,
  disabled,
  couponCode,
  onCouponCodeChange,
  couponLoading,
  couponResult,
  onApplyCoupon,
  onClearCoupon,
  scheduledPlanId,
  scheduledBillingPeriod,
  onCancelScheduledChange,
  excludePlanIds,
}: PricingCardsProps) {
  const {
    billingCycle,
    toggleBillingCycle,
    t,
    language,
    currencySymbol,
    getDisplayPrice,
    getAnnualTotalDisplay,
    getMonthlyOriginalPrice,
    getMonthlySavingsPercent,
    getValueSavingsVsStarterPercent,
  } = usePricingCards();
  const { profile } = useAuth();
  const [couponExpanded, setCouponExpanded] = useState(false);
  // Only show current plan indicator after profile has loaded (prevents flash of "free")
  const currentPlanId = profile?.subscription_name ?? null;
  const currentBillingPeriod = profile?.billing_period ?? null;
  const subscriptionStatus = profile?.subscription_status ?? null;
  const currentPeriodEnd = profile?.current_period_end ?? null;
  const isCancelledPlan = subscriptionStatus === "cancelled";
  const hasActivePaidSub = currentPlanId && currentPlanId !== "free"
    && subscriptionStatus !== "cancelled" && subscriptionStatus !== "expired";

  const formatExpiryDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString(
      language === "he" ? "he-IL" : "en-US",
      {
        month: "short",
        day: "numeric",
      },
    );

  return (
    <div>
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-6 mb-8">
        <span
          className={cn(
            "text-sm font-semibold transition-colors",
            billingCycle === "monthly"
              ? "text-foreground"
              : "text-muted-foreground",
          )}
        >
          {t.pricing.monthly}
        </span>
        <button
          onClick={toggleBillingCycle}
          className="w-16 h-8 bg-secondary rounded-full relative p-1.5 transition-colors border border-border"
        >
          <div
            className={cn(
              "w-5 h-5 bg-primary rounded-full transition-all duration-300",
              billingCycle === "annual"
                ? "ltr:translate-x-[32px] rtl:translate-x-[-32px]"
                : "translate-x-0",
            )}
          ></div>
        </button>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-sm font-semibold transition-colors",
              billingCycle === "annual"
                ? "text-foreground"
                : "text-muted-foreground",
            )}
          >
            {t.pricing.annual}
          </span>
          <span
            className={cn(
              "text-[10px] px-2.5 py-1 rounded-full border font-bold",
              billingCycle === "annual"
                ? "bg-green-500/10 text-green-400 border-green-500/20"
                : "bg-green-500/5 text-green-400/70 border-green-500/10",
            )}
          >
            {t.pricing.discount20}
          </span>
        </div>
      </div>

      {/* Coupon Section (full mode only, hidden for users with active paid subs — coupons can't apply to prorated upgrades) */}
      {SHOW_COUPON_UI && mode === "full" && !hasActivePaidSub && (
        <div className="flex justify-center mb-8">
          {couponResult?.valid ? (
            <div className="flex flex-col items-center gap-1.5">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20">
                <Tag className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400">
                  {couponResult.discountPercent && couponResult.discountPercent > 0
                    ? t.pricing.couponValidPercentMonthly
                        .replace("{percent}", String(couponResult.discountPercent))
                    : t.pricing.couponValidAmountMonthly
                        .replace("{amount}", String(couponResult.discountAmount ?? 0))
                  }
                </span>
                <button
                  onClick={onClearCoupon}
                  className="ml-1 p-0.5 rounded-full hover:bg-green-500/20 transition-colors"
                  aria-label={t.pricing.couponRemove}
                >
                  <X className="w-3.5 h-3.5 text-green-400" />
                </button>
              </div>
              {COUPON_BLOCKED_FOR_ANNUAL && billingCycle === "annual" && (
                <span className="text-xs text-amber-400">
                  {t.pricing.couponMonthlyOnly}
                </span>
              )}
            </div>
          ) : couponExpanded ? (
            <div className="flex flex-col items-center gap-2 w-full max-w-sm">
              <div className="flex items-center gap-2 w-full">
                <Input
                  value={couponCode ?? ""}
                  onChange={(e) => onCouponCodeChange?.(e.target.value)}
                  placeholder={t.pricing.couponPlaceholder}
                  className="flex-1 text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onApplyCoupon?.();
                    }
                  }}
                  disabled={couponLoading}
                />
                <Button
                  variant="primary"
                  onClick={onApplyCoupon}
                  disabled={couponLoading || !couponCode?.trim()}
                  className="px-8 py-2 shrink-0 text-sm font-semibold"
                >
                  {couponLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    t.pricing.couponApply
                  )}
                </Button>
              </div>
              {couponResult && !couponResult.valid && (
                <p className="text-sm text-red-400">
                  {couponResult.message ?? t.pricing.couponInvalid}
                </p>
              )}
            </div>
          ) : (
            <button
              onClick={() => setCouponExpanded(true)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
            >
              {t.pricing.couponHave}
            </button>
          )}
        </div>
      )}

      {/* Pricing Cards Grid */}
      <div
        className={cn(
          "grid md:grid-cols-2 gap-6",
          // Keep a balanced grid when FREE is hidden (e.g. the credit-wall modal).
          PLANS.filter((p) => !excludePlanIds?.includes(p.id)).length >= 4
            ? "lg:grid-cols-4"
            : "lg:grid-cols-3",
        )}
      >
        {PLANS.map((plan, planIndex) => {
          // Hidden plans (e.g. FREE in the modal) still occupy their PLANS index
          // so upgrade/downgrade comparisons below stay correct.
          if (excludePlanIds?.includes(plan.id)) return null;
          const displayPrice = getDisplayPrice(plan);
          const annualTotal = getAnnualTotalDisplay(plan.monthlyPrice, plan);
          const specificFeatures =
            PLAN_SPECIFIC_FEATURES[
              plan.id as keyof typeof PLAN_SPECIFIC_FEATURES
            ];
          const isCurrentPlan =
            plan.id === currentPlanId &&
            (plan.id === "free" ||
              billingCycle === (currentBillingPeriod ?? "monthly"));
          const hasBadge = plan.badge || isCurrentPlan;
          const currentPlanIndex = currentPlanId
            ? PLANS.findIndex((p) => p.id === currentPlanId)
            : -1;
          const hasActiveSub =
            currentPlanId &&
            currentPlanId !== "free" &&
            subscriptionStatus !== "cancelled" &&
            subscriptionStatus !== "expired";
          const isUpgrade = hasActiveSub && planIndex > currentPlanIndex;
          const isDowngrade = hasActiveSub && planIndex < currentPlanIndex;
          const isScheduledPlan =
            !!scheduledPlanId &&
            plan.id === scheduledPlanId &&
            billingCycle === scheduledBillingPeriod;
          const discount = computeDiscount(plan, displayPrice, annualTotal, couponResult, billingCycle, language);
          // Single savings figure for both modes so the badge renders identically
          const savingsPercent =
            billingCycle === "annual"
              ? getMonthlySavingsPercent(plan)
              : getValueSavingsVsStarterPercent(plan);

          return (
            <div
              key={plan.id}
              className={cn(
                "bg-card rounded-3xl p-6 transition-all duration-300 flex flex-col text-start relative group hover:translate-y-[-4px] border-t-2",
                plan.accent.topBorder,
                isCurrentPlan && isCancelledPlan
                  ? "border-x border-b border-amber-500/50 ring-1 ring-amber-500/20"
                  : isCurrentPlan
                    ? "border-x border-b border-green-500/50 ring-1 ring-green-500/20"
                    : isScheduledPlan
                      ? "border-x border-b border-amber-500/50 ring-1 ring-amber-500/20"
                      : plan.popular
                        ? "border-x border-b border-brand/30"
                        : "border-x border-b border-border",
                // PRO floats above its neighbours as the hero plan
                plan.id === "pro" &&
                  "bg-gradient-to-b from-brand/[0.07] to-card ring-1 ring-brand/30 shadow-2xl shadow-brand/25 lg:scale-[1.04] lg:z-10",
              )}
            >
              {/* Badge: Cancelled Plan > Current Plan > Scheduled > Plan Badge */}
              {isCurrentPlan && isCancelledPlan && currentPeriodEnd ? (
                <div className="absolute -top-3 end-6 text-[10px] font-black px-3 py-1 rounded-full tracking-widest shadow-lg bg-amber-500 text-white">
                  {"⏳ " +
                    t.pricing.activeUntilDate.replace(
                      "{date}",
                      formatExpiryDate(currentPeriodEnd),
                    )}
                </div>
              ) : isCurrentPlan ? (
                <div className="absolute -top-3 end-6 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg bg-green-500 text-white">
                  {t.pricing.currentPlan}
                </div>
              ) : isScheduledPlan ? (
                <div className="absolute -top-3 end-6 text-[10px] font-black px-3 py-1 rounded-full tracking-widest shadow-lg bg-amber-500 text-white">
                  {t.pricing.scheduledBadge.replace("{date}",
                    currentPeriodEnd
                      ? new Date(currentPeriodEnd).toLocaleDateString(
                          language === "he" ? "he-IL" : "en-US",
                          { month: "short", day: "numeric" },
                        )
                      : "")}
                </div>
              ) : plan.badge ? (
                <div
                  className={cn(
                    "absolute -top-3 end-6 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest shadow-lg",
                    plan.id === "creator"
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                      : "gradient-primary text-white",
                  )}
                >
                  {plan.id !== "creator" && "💎 "}
                  {plan.badge[language]}
                </div>
              ) : null}
              <h3
                className={cn(
                  "text-2xl font-bold mb-2",
                  plan.accent.titleText ?? plan.accent.text,
                )}
              >
                {plan.name}
              </h3>
              <div className="mb-6">
                {discount.hasDiscount ? (
                  <>
                    <div>
                      <span className="text-2xl font-bold text-muted-foreground/60 line-through">
                        {currencySymbol}{displayPrice}
                      </span>
                    </div>
                    <span className="text-5xl font-black text-green-400 tracking-tighter">
                      {discount.discountedPrice === 0
                        ? t.pricing.freeLabel
                        : <>{currencySymbol}{discount.discountedPrice}</>}
                    </span>
                    {plan.monthlyPrice > 0 && (
                      <span className="text-muted-foreground text-sm font-medium">
                        {" "}
                        {t.pricing.monthLabel}
                      </span>
                    )}
                    <div className="mt-1.5 text-xs text-muted-foreground">
                      {t.pricing.couponThenPrice
                        .replace("{symbol}", currencySymbol)
                        .replace("{price}", String(displayPrice))
                        .replace("{period}", billingCycle === "annual" ? t.pricing.periodYear : t.pricing.periodMonth)}
                    </div>
                    {billingCycle === "annual" && (
                      <div className="mt-1 text-muted-foreground text-sm">
                        {t.pricing.billedAnnually
                          .replace("{symbol}", currencySymbol)
                          .replace("{total}", String(discount.discountedAnnualTotal))}
                        {" "}
                        <span className="line-through text-muted-foreground/50">
                          {currencySymbol}{annualTotal}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Strikethrough old price: rendered on annual for every
                        card so the price block has a consistent height. For
                        plans without an original price (FREE), the slot stays
                        invisible — it still takes vertical space, keeping the
                        tokens box below aligned across the four cards. */}
                    {billingCycle === "annual" && (
                      <div
                        className={cn(
                          "text-2xl font-bold text-muted-foreground/60 line-through leading-tight",
                          plan.monthlyPrice === 0 && "invisible",
                        )}
                      >
                        {currencySymbol}
                        {plan.monthlyPrice > 0 ? getMonthlyOriginalPrice(plan) : "0"}
                      </div>
                    )}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
                      <span className="text-5xl font-black text-foreground tracking-tighter">
                        {currencySymbol}
                        {displayPrice}
                      </span>
                      {plan.monthlyPrice > 0 && (
                        <span className="text-muted-foreground text-sm font-medium">
                          {t.pricing.monthLabel}
                        </span>
                      )}
                    </div>
                    {/* Savings badge slot: always rendered so every card has
                        the same price-block height, even when a given plan
                        has nothing to save (FREE always; STARTER in monthly
                        view). `invisible` preserves layout flow. */}
                    <div className="mt-2">
                      <span
                        className={cn(
                          "inline-block text-[10px] px-2.5 py-1 rounded-full border font-bold bg-green-500/10 text-green-400 border-green-500/20",
                          !(plan.monthlyPrice > 0 && savingsPercent > 0) && "invisible",
                        )}
                      >
                        {savingsPercent > 0
                          ? t.pricing.savePercent.replace(
                              "{percent}",
                              String(savingsPercent),
                            )
                          : "0%"}
                      </span>
                    </div>
                  </>
                )}
              </div>
              <div className="bg-secondary rounded-2xl p-4 mb-8 text-center border border-border">
                <span className={cn("font-black text-2xl", plan.accent.text)}>
                  {plan.tokens.toLocaleString()}
                </span>
                <span className="text-muted-foreground text-sm block">
                  {plan.monthlyPrice > 0 ? t.pricing.tokensPerMonth : t.pricing.tokens}
                </span>
                <div className="flex flex-col gap-1.5 mt-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <ImageIcon
                      className={cn(
                        "w-3.5 h-3.5 shrink-0 opacity-70",
                        plan.accent.text,
                      )}
                    />
                    <span>
                      {(() => {
                        const r = estimateRange(
                          plan.tokens,
                          GENERATION_ESTIMATES.image.minCredits,
                          GENERATION_ESTIMATES.image.maxCredits,
                        );
                        return t.pricing.approxImagesRange
                          .replace("{min}", String(r.min))
                          .replace("{max}", String(r.max));
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Film
                      className={cn(
                        "w-3.5 h-3.5 shrink-0 opacity-70",
                        plan.accent.text,
                      )}
                    />
                    <span>
                      {(() => {
                        const r = estimateRange(
                          plan.tokens,
                          GENERATION_ESTIMATES.shortVideo.minCredits,
                          GENERATION_ESTIMATES.shortVideo.maxCredits,
                        );
                        return t.pricing.approxShortVideosRange
                          .replace("{min}", String(r.min))
                          .replace("{max}", String(r.max));
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Plan Specific + Shared Features */}
              <ul className="flex-1 space-y-3 mb-10">
                {specificFeatures.map((feat, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm leading-snug"
                  >
                    {feat.included ? (
                      <>
                        <div
                          className={cn(
                            "w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 border",
                            plan.accent.bgSubtle,
                            plan.accent.borderSubtle,
                          )}
                        >
                          <Check className={cn("w-3 h-3", plan.accent.text)} />
                        </div>
                        <span className="text-muted-foreground">
                          {feat.text[language]}
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-5 h-5 bg-red-500/10 rounded-full flex items-center justify-center shrink-0 mt-0.5 border border-red-500/20">
                          <X className="w-3 h-3 text-red-400" />
                        </div>
                        <span className="text-muted-foreground/60">
                          {feat.text[language]}
                        </span>
                      </>
                    )}
                  </li>
                ))}
                {SHARED_FEATURES.map((feat, i) => (
                  <li
                    key={`shared-${i}`}
                    className="flex items-start gap-3 text-sm leading-snug"
                  >
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 border",
                        plan.accent.bgSubtle,
                        plan.accent.borderSubtle,
                      )}
                    >
                      <Check className={cn("w-3 h-3", plan.accent.text)} />
                    </div>
                    <span className="text-muted-foreground">
                      {feat.text[language]}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {mode === "landing" ? (
                plan.id === "free" && currentPlanId === "free" ? (
                  <Link to="/generate-product-catalog">
                    <Button
                      variant="secondary"
                      className={cn(
                        "w-full",
                        plan.accent.ctaBg,
                        plan.accent.ctaText,
                      )}
                    >
                      {t.pricing.goToStudio}
                    </Button>
                  </Link>
                ) : (
                  <Link to="/pricing">
                    <Button
                      variant="secondary"
                      className={cn(
                        "w-full",
                        plan.accent.ctaBg,
                        plan.accent.ctaText,
                      )}
                    >
                      {plan.cta[language]}
                    </Button>
                  </Link>
                )
              ) : isCurrentPlan && isCancelledPlan ? (
                <Button
                  variant="secondary"
                  className="w-full bg-amber-500 hover:bg-amber-400 text-white font-bold"
                  onClick={() =>
                    onSelectPlan?.(plan.id, plan.name, billingCycle)
                  }
                  disabled={disabled}
                >
                  {t.pricing.resubscribe}
                </Button>
              ) : isCurrentPlan && !!scheduledPlanId ? (
                <Button
                  variant="secondary"
                  className="w-full bg-green-500 hover:bg-green-400 text-white font-bold"
                  onClick={onCancelScheduledChange}
                  disabled={disabled}
                >
                  {t.pricing.keepThisPlan}
                </Button>
              ) : isCurrentPlan ? (
                <Button
                  variant="secondary"
                  className="w-full bg-green-500 text-white font-bold cursor-default hover:bg-green-500"
                  disabled
                >
                  {t.pricing.currentPlan}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className={cn(
                    "w-full",
                    plan.accent.ctaBg,
                    plan.accent.ctaText,
                  )}
                  onClick={() =>
                    onSelectPlan?.(plan.id, plan.name, billingCycle)
                  }
                  disabled={disabled}
                >
                  {isUpgrade
                    ? t.pricing.upgradeTo.replace("{plan}", plan.name)
                    : isDowngrade
                      ? t.pricing.downgradeTo.replace("{plan}", plan.name)
                      : plan.cta[language]}
                  {billingCycle === "annual" && plan.monthlyPrice > 0
                    ? ` ${t.pricing.annualSuffix}`
                    : ""}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
